// Matomo tracker — mirrors the meinOrt app's tracking scheme (see
// docs/superpowers/specs/2026-07-09-matomo-tracking-design.md).
// Config, location context and page route are injected per page by
// MatomoHelper as <script type="application/json" id="matomo-context">.
// Tracking must never break the page: every entry point is guarded and
// only logs via console.debug when devLogging is on.

const FORBIDDEN_SUBSTRINGS = ['token', 'email', 'password', 'address'];
const EMAIL_PATTERN = /\S+@\S+\.\S+/;
const MAX_LABEL_LENGTH = 100;
const MAX_PENDING_CALLS = 100;
const DEFAULT_CATEGORY = 'App';
// Visit-scoped custom dimension IDs — identical on the Android/iOS/web sites.
// Every value is also sent under ID + 5 (6–10), where the same dimensions are
// configured action-scoped: visit-scoped dimensions keep only the LAST value
// per visit, so a mid-session Ort switch re-attributes the whole visit, while
// the action-scoped copies stay correct per hit.
const DIMENSION_IDS = {
  payingLocation: 1,
  payingLocationName: 2,
  locationType: 3,
  parentVg: 4,
  pushEnabled: 5,
};
const ACTION_SCOPE_OFFSET = 5;
const CONTEXT_STORAGE_KEY = 'matomo:locationContext';
const LAST_ORT_SLUG_KEY = 'matomo:lastOrtSlug';

window._paq = window._paq || [];

let configured = false;
let devLogging = false;
let locationContext = null;
let currentRoute = null;
let pageLoadedAt = null;
let dwellTracked = true;
const pendingCalls = [];

function logDebug(...args) {
  if (devLogging) { console.debug('[matomo]', ...args); }
}

function readInitialConsent() {
  try {
    return !!(window.klaro && window.klaroConfig &&
      window.klaro.getManager(window.klaroConfig).consents.matomo);
  } catch (e) {
    return false;
  }
}

let consentGiven = readInitialConsent();

function readStorage(key) {
  try { return window.localStorage.getItem(key); } catch (e) { return null; }
}

function writeStorage(key, value) {
  try { window.localStorage.setItem(key, value); } catch (e) { /* quota/private mode */ }
}

function readPageData() {
  const tag = document.getElementById('matomo-context');
  if (!tag) { return null; }
  try { return JSON.parse(tag.textContent); } catch (e) { return null; }
}

function configure(config) {
  if (configured || !config) { return; }
  configured = true;
  devLogging = !!config.devLogging;
  const base = String(config.serverUrl || 'https://matomo.meinort.de/').replace(/\/*$/, '/');
  window._paq.push(['setTrackerUrl', `${base}matomo.php`]);
  window._paq.push(['setSiteId', String(config.siteId)]);
  window._paq.push(['alwaysUseSendBeacon']);
}

function isSafeLabel(value) {
  if (typeof value !== 'string' || value.length === 0) { return false; }
  if (value.length > MAX_LABEL_LENGTH) { return false; }
  if (EMAIL_PATTERN.test(value)) { return false; }
  const lower = value.toLowerCase();
  return !FORBIDDEN_SUBSTRINGS.some((word) => lower.includes(word));
}

function pushEnabledValue() {
  if (!('Notification' in window)) { return null; }
  if (window.Notification.permission === 'granted') { return 'enabled'; }
  if (window.Notification.permission === 'denied') { return 'disabled'; }
  return null; // permission not decided yet -> omit the dimension entirely
}

// Set before EVERY hit; unknown values are deleted so nothing stale leaks
// across Turbo visits.
function applyDimensions() {
  const values = {
    [DIMENSION_IDS.payingLocation]: locationContext && locationContext.ortSlug,
    [DIMENSION_IDS.payingLocationName]: locationContext && locationContext.ortName,
    [DIMENSION_IDS.locationType]: locationContext && locationContext.locationType,
    [DIMENSION_IDS.parentVg]: locationContext && locationContext.parentVg,
    [DIMENSION_IDS.pushEnabled]: pushEnabledValue(),
  };
  Object.keys(values).forEach((id) => {
    [Number(id), Number(id) + ACTION_SCOPE_OFFSET].forEach((dimensionId) => {
      if (values[id]) {
        window._paq.push(['setCustomDimension', dimensionId, values[id]]);
      } else {
        window._paq.push(['deleteCustomDimension', dimensionId]);
      }
    });
  });
}

function sendPageView(route, title) {
  applyDimensions();
  const pagePath = `${locationContext.ortSlug}/${route}`;
  window._paq.push(['setCustomUrl', `${window.location.origin}/${pagePath}`]);
  window._paq.push(['setDocumentTitle', title]);
  window._paq.push(['trackPageView']);
  logDebug('pageview', pagePath);
}

function sendEvent(category, action, name, value) {
  const label = name || action;
  if (![category, action, label].every(isSafeLabel)) {
    logDebug('trackEvent: dropped call with unsafe label', category, action, label);
    return;
  }
  applyDimensions();
  const args = ['trackEvent', category, action, label];
  const numericValue = Number(value);
  if (value !== undefined && value !== null && Number.isFinite(numericValue)) {
    args.push(numericValue);
  }
  window._paq.push(args);
  logDebug('event', category, action, label, value);
}

// Calls made before an Ort context exists are queued (cap 100, drop-oldest)
// and flushed FIFO once a context is available — same behavior as the app.
function enqueue(call) {
  if (pendingCalls.length >= MAX_PENDING_CALLS) {
    pendingCalls.shift();
    logDebug('queue overflow: dropped oldest pending call');
  }
  pendingCalls.push(call);
}

function flushPendingCalls() {
  while (pendingCalls.length > 0 && locationContext) {
    const call = pendingCalls.shift();
    if (call.kind === 'pageview') {
      sendPageView(call.route, call.title);
    } else {
      sendEvent(call.category, call.action, call.name, call.value);
    }
  }
}

function trackPageView(route, title) {
  if (!consentGiven) { return; }
  if (!locationContext) { enqueue({ kind: 'pageview', route, title }); return; }
  sendPageView(route, title);
}

function trackEvent(category, action, name, value) {
  if (!consentGiven) { return; }
  if (!locationContext) { enqueue({ kind: 'event', category, action, name, value }); return; }
  sendEvent(category, action, name, value);
}

function trackCurrentPage() {
  if (currentRoute === null) { return; }
  trackPageView(currentRoute, document.title);
}

function setLocationContext(context) {
  locationContext = context;
  writeStorage(CONTEXT_STORAGE_KEY, JSON.stringify(context));
  const previousSlug = readStorage(LAST_ORT_SLUG_KEY);
  if (previousSlug !== context.ortSlug) {
    writeStorage(LAST_ORT_SLUG_KEY, context.ortSlug);
    trackEvent(DEFAULT_CATEGORY, 'select_location', context.ortSlug);
  }
  flushPendingCalls();
}

function handlePageLoad() {
  try {
    const data = readPageData();
    if (!data) { return; } // layout without tracking (e.g. welcome page)
    configure(data.config);
    if (data.context) {
      setLocationContext(data.context);
    } else if (!locationContext) {
      // No Ort on this page: fall back to the last persisted context,
      // mirroring the app's loadLocationContext() on startup.
      const stored = readStorage(CONTEXT_STORAGE_KEY);
      if (stored) {
        try { locationContext = JSON.parse(stored); } catch (e) { locationContext = null; }
      }
      if (locationContext) { flushPendingCalls(); }
    }
    currentRoute = data.route;
    pageLoadedAt = Date.now();
    dwellTracked = false;
    trackPageView(data.route, document.title);
    trackEvent(DEFAULT_CATEGORY, 'visit');
    if (data.pageEvent) { trackEvent(DEFAULT_CATEGORY, data.pageEvent); }
  } catch (e) {
    logDebug('handlePageLoad failed', e);
  }
}

function trackDwellTime() {
  try {
    if (dwellTracked || pageLoadedAt === null) { return; }
    dwellTracked = true;
    const seconds = Math.max(0, Math.round((Date.now() - pageLoadedAt) / 1000));
    const label = (locationContext && currentRoute !== null) ?
      `${locationContext.ortSlug}/${currentRoute}` : undefined;
    trackEvent(DEFAULT_CATEGORY, 'dwell_time', label, seconds);
  } catch (e) {
    logDebug('trackDwellTime failed', e);
  }
}

function handleContactClick(event) {
  try {
    const target = event.target instanceof Element ? event.target : null;
    const link = target && target.closest('a[href^="mailto:"], a[href^="tel:"]');
    if (!link) { return; }
    const type = link.getAttribute('href').startsWith('mailto:') ? 'email' : 'phone';
    trackEvent(DEFAULT_CATEGORY, 'contact_click', type);
  } catch (e) {
    logDebug('contact_click handler failed', e);
  }
}

function enableTracking() {
  consentGiven = true;
  window._paq.push(['rememberCookieConsentGiven', 365]);
  trackCurrentPage();
}

function disableTracking() {
  window._paq.push(['forgetCookieConsentGiven']);
  consentGiven = false;
}

function guarded(fn) {
  return (...args) => {
    try { return fn(...args); } catch (e) { logDebug('tracking call failed', e); return undefined; }
  };
}

document.addEventListener('turbo:load', handlePageLoad);
document.addEventListener('turbo:before-visit', trackDwellTime);
window.addEventListener('pagehide', trackDwellTime);
document.addEventListener('click', handleContactClick);

window.matomoTracker = {
  trackPageView: guarded(trackCurrentPage),
  trackEvent: guarded(trackEvent),
  enableTracking: guarded(enableTracking),
  disableTracking: guarded(disableTracking),
};
